import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Paper, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconArrowUpRight } from '@tabler/icons-react';

// ERPNext "Reports & Masters" pattern: a heading, then a responsive grid of
// titled cards, each a vertical list of links with a small up-right arrow.
// Wheels-branded + RTL (the arrow uses a logical rotate so it points
// out-of-page in both directions). Links to existing pages; a link with
// `soon: true` renders muted/non-navigating for not-yet-built masters.

export interface WorkspaceLink {
  labelKey: string;
  to?: string;
  soon?: boolean;
}

export interface WorkspaceGroup {
  titleKey: string;
  links: WorkspaceLink[];
}

interface Props {
  headingKey?: string;
  groups: WorkspaceGroup[];
}

function LinkRow({ link }: { link: WorkspaceLink }) {
  const { t } = useTranslation();
  const content = (
    <div className="group flex items-center gap-1.5 py-1.5">
      <Text
        size="sm"
        c={link.soon ? 'dimmed' : 'var(--wheels-ink)'}
        className={link.soon ? '' : 'group-hover:text-[var(--wheels-accent)] transition-colors'}
      >
        {t(link.labelKey)}
      </Text>
      {link.soon ? (
        <Text size="10px" c="dimmed" className="rounded bg-[var(--wheels-bg)] px-1 py-0.5">
          {t('workspace.soon')}
        </Text>
      ) : (
        <IconArrowUpRight
          size={13}
          stroke={1.8}
          className="text-[var(--wheels-ink-muted)] opacity-0 group-hover:opacity-100 transition-opacity rtl:-scale-x-100"
        />
      )}
    </div>
  );

  if (link.soon || !link.to) {
    return <div>{content}</div>;
  }
  return (
    <Link to={link.to} className="block no-underline">
      {content}
    </Link>
  );
}

export function WorkspaceLinks({ headingKey, groups }: Props) {
  const { t } = useTranslation();
  return (
    <Stack gap="sm">
      {headingKey && (
        <Text fw={700} size="lg" c="var(--wheels-ink-strong)">
          {t(headingKey)}
        </Text>
      )}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {groups.map((group) => (
          <Paper key={group.titleKey} p="md" withBorder radius="md">
            <Text fw={600} size="sm" c="var(--wheels-ink-strong)" mb={4}>
              {t(group.titleKey)}
            </Text>
            {group.links.map((link) => (
              <LinkRow key={link.labelKey} link={link} />
            ))}
          </Paper>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
